import { INVITE_STEP } from '../constants/inviteRules.js';
import { USER_TYPE } from '../constants/copy.js';

export const initialInviteState = Object.freeze({
  currentStep: INVITE_STEP.PEOPLE,
  /** Text typed in the emails field and not yet confirmed (comma / Enter / Next). */
  draft: '',
  /** Valid, new addresses: the people who get an invitation. */
  emails: [],
  /** Entries that are not an address, waiting to be corrected or removed. */
  invalid: [],
  /** Addresses already in the workspace: `{ email, name, status }`. Never sent. */
  existing: [],
  /** Optional display names, by email. */
  names: {},
  userType: USER_TYPE.MEMBER,
  roleIds: [],
  groupIds: [],
  sendEmail: true,
  note: '',
});

const union = (current, incoming = []) => [...new Set([...current, ...incoming])];
const toggle = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

/** Wizard state of the Invite members builder. Pure; the step sequence lives in `inviteSteps`. */
export const inviteMemberReducer = (state, action) => {
  switch (action.type) {
    case 'addEmails': {
      const existingEmails = new Set(state.existing.map((e) => e.email));
      return {
        ...state,
        emails: union(state.emails, action.valid),
        invalid: union(state.invalid, action.invalid),
        existing: [
          ...state.existing,
          ...(action.existing ?? []).filter((e) => !existingEmails.has(e.email)),
        ],
      };
    }
    case 'removeEmail': {
      const names = Object.fromEntries(
        Object.entries(state.names).filter(([email]) => email !== action.email),
      );
      return { ...state, emails: state.emails.filter((e) => e !== action.email), names };
    }
    case 'removeInvalid':
      return { ...state, invalid: state.invalid.filter((v) => v !== action.value) };
    case 'removeExisting':
      return { ...state, existing: state.existing.filter((e) => e.email !== action.email) };
    case 'setDraft':
      return { ...state, draft: action.value };
    case 'setName':
      return { ...state, names: { ...state.names, [action.email]: action.name } };
    case 'setUserType':
      return action.userType === state.userType
        ? state
        : { ...state, userType: action.userType, roleIds: [], groupIds: [] };
    case 'toggleRole':
      return { ...state, roleIds: toggle(state.roleIds, action.id) };
    case 'toggleGroup':
      return { ...state, groupIds: toggle(state.groupIds, action.id) };
    case 'setSendEmail':
      return { ...state, sendEmail: action.value };
    case 'setNote':
      return { ...state, note: action.value };
    case 'goToStep':
      return { ...state, currentStep: action.step };
    default:
      return state;
  }
};
